import { Badge, Card, Image } from 'antd';
import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { theme } from '../../constants/theme';
import { CallForTender } from '../../slices/callForTender';
import { CalendarOutlined } from '@ant-design/icons';

const useStyles = createUseStyles({
  title: {
    fontFamily: "'Baloo 2'",
    fontSize: '18px !important',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis !important',
    display: '-webkit-box !important',
    '-webkit-line-clamp': '2 !important',
    '-webkit-box-orient': 'vertical !important',
    lineHeight: '24px !important',
    maxHeight: '54px !important',
    width: '100%',
    whiteSpace: 'normal !important',
  },
  description: {
    fontSize: 13,
    color: '#666 !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    lineHeight: '18px',
    maxHeight: '36px',
    marginTop: '4px',
  },
  donorName: {
    fontSize: 12,
    color: theme.palette.primary,
    fontWeight: 500,
    marginTop: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  closingDateContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '8px',
  },
  closingDateIcon: {
    fontSize: '12px',
    color: '#737373',
  },
  closingDateText: {
    fontSize: '12px',
    color: '#737373',
  },
  urgentDate: {
    color: '#ff4d4f',
  },
  cardContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});

const formatClosingDate = (date: Date | string | undefined): string => {
  if (!date) return 'Permanent';
  const closingDate = new Date(date);
  return closingDate.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const isUrgent = (date: Date | string | undefined): boolean => {
  if (!date) return false;
  const closingDate = new Date(date);
  const now = new Date();
  const diffInDays = Math.ceil(
    (closingDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diffInDays <= 14;
};

export const CallForTenderCard = ({
  callForTender,
  redirectNewPage = false,
  disableClick = false,
  onPaywallRedirect,
}: {
  callForTender: CallForTender;
  redirectNewPage?: boolean;
  disableClick?: boolean;
  onPaywallRedirect?: () => void;
}) => {
  const history = useHistory();
  const classes = useStyles();
  const defaultImage = '/images/donorCardDefault.svg';
  const donor = callForTender.adminDonors?.[0];
  const initialImage = donor?.ogImageUrl || donor?.logoUrl || defaultImage;
  const [imgUrl, setImgUrl] = useState(initialImage);

  const donorName = donor?.name || 'Mécène';
  const urgent = isUrgent(callForTender.closingDate);

  const handleClick = () => {
    if (disableClick) {
      onPaywallRedirect?.();
      return;
    }

    const donorId = donor?._id;
    if (!donorId) return;

    const url = `/donor/${donorId}/callForTender/${callForTender._id}`;
    if (redirectNewPage) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  const cardContent = (
    <Card
      hoverable={!disableClick}
      style={{
        width: 250,
        height: 330,
        marginBottom: '2dvh',
        borderRadius: 8,
        boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
        opacity: disableClick ? 0.7 : 1,
      }}
      cover={
        <Image
          preview={false}
          alt="donor"
          style={{
            width: 250,
            height: 130,
            objectFit: imgUrl === defaultImage ? 'cover' : 'contain',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
          src={imgUrl}
          onError={() => setImgUrl(defaultImage)}
        />
      }
      onClick={handleClick}
    >
      <div className={classes.cardContent}>
        <div className={classes.title}>{callForTender.name}</div>
        {callForTender.description && (
          <div className={classes.description}>{callForTender.description}</div>
        )}
        <div className={classes.donorName}>{donorName}</div>
        <div className={classes.closingDateContainer}>
          <CalendarOutlined
            className={`${classes.closingDateIcon} ${
              urgent ? classes.urgentDate : ''
            }`}
          />
          <span
            className={`${classes.closingDateText} ${
              urgent ? classes.urgentDate : ''
            }`}
          >
            {callForTender.closingDate
              ? `Clôture: ${formatClosingDate(callForTender.closingDate)}`
              : 'Permanent'}
          </span>
        </div>
      </div>
    </Card>
  );

  return urgent && callForTender.closingDate ? (
    <Badge.Ribbon text="Urgent" color="#ff4d4f">
      {cardContent}
    </Badge.Ribbon>
  ) : (
    cardContent
  );
};
